import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { walletManager, WALLET_TYPES, getAccounts } from '../EthersSetup';
import { AddressDisplayLong } from '../AddressDisplay';
import QRModal from './QRModal';
import QRCodeService from '../../services/QRCodeService';
import { ethers } from 'ethers';

/**
 * WalletConfigIcon Component
 * 
 * Compact wallet status icon for top-right placement throughout the app.
 * Shows wallet connection status and provides quick access to wallet management.
 */
const WalletConfigIcon = ({ onDisconnect }) => {
  const navigate = useNavigate();
  const [walletInfo, setWalletInfo] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('below'); // 'below' or 'above'
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingBalance, setLoadingBalance] = useState(false);

  // Calculate optimal dropdown position based on viewport space
  const calculateDropdownPosition = () => {
    if (!buttonRef.current) return 'below';

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 400; // Estimated dropdown height

    // Check if there's enough space below
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    // Position above if not enough space below and more space above
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      return 'above';
    }

    return 'below';
  };
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [shareSuccess, setShareSuccess] = useState('');

  useEffect(() => {
    loadWalletInfo();

    const handleWalletChange = () => {
      loadWalletInfo();
    };

    // Direct MetaMask account change listener for more reliable detection
    const handleMetaMaskAccountsChanged = (accounts) => {
      console.log('🔄 MetaMask accounts changed directly:', accounts);
      loadWalletInfo();
    };

    walletManager.on('providerChanged', handleWalletChange);
    walletManager.on('accountsChanged', handleWalletChange);
    walletManager.on('disconnect', handleWalletChange);

    // Add direct MetaMask listener if available
    if (typeof window !== 'undefined' && window.ethereum) {
      window.ethereum.on('accountsChanged', handleMetaMaskAccountsChanged);
    }

    return () => {
      walletManager.off('providerChanged', handleWalletChange);
      walletManager.off('accountsChanged', handleWalletChange);
      walletManager.off('disconnect', handleWalletChange);

      // Remove direct MetaMask listener
      if (typeof window !== 'undefined' && window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleMetaMaskAccountsChanged);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchBalance = async (address) => {
    if (!address) return null;
    
    try {
      setLoadingBalance(true);
      const provider = walletManager.getProvider();
      if (!provider) return null;
      
      const balanceWei = await provider.getBalance(address);
      const balanceEth = ethers.utils.formatEther(balanceWei);
      setBalance(balanceEth);
    } catch (error) {
      console.error('Error fetching balance:', error);
      setBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  const fetchRecentTransactions = async (address) => {
    if (!address) return;

    try {
      setLoadingTransactions(true);

      // OPTIMIZATION: Disable expensive block scanning to reduce API calls
      // This was causing hundreds of getBlockWithTransactions() calls
      // which are extremely expensive and hit RPC rate limits

      // For now, set empty transactions array
      // Future enhancement: Use Etherscan API or event-based transaction history
      console.log('🚀 Transaction history disabled for performance - no expensive block scanning');
      setTransactions([]);

    } catch (error) {
      console.error('Error in transaction fetching (disabled):', error);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const loadWalletInfo = async (retryCount = 0) => {
    try {
      setIsLoading(true);
      const activeProvider = walletManager.getActiveProvider();

      if (activeProvider) {
        const accounts = await getAccounts();
        const address = accounts[0] || null;
        const walletType = activeProvider.getType();

        // For MetaMask, if we don't get accounts immediately after connection, retry a few times
        if (!address && walletType === WALLET_TYPES.METAMASK && activeProvider.isConnected() && retryCount < 3) {
          console.log(`🔄 MetaMask connected but no accounts yet, retrying... (attempt ${retryCount + 1})`);
          setIsLoading(false);
          // Retry after progressively longer delays
          const delay = 500 * (retryCount + 1); // 500ms, 1000ms, 1500ms
          setTimeout(() => loadWalletInfo(retryCount + 1), delay);
          return;
        }

        // If we still don't have an address after retries but MetaMask claims to be connected,
        // show debug info to help troubleshoot
        if (!address && walletType === WALLET_TYPES.METAMASK && activeProvider.isConnected() && retryCount >= 3) {
          console.warn('🚨 MetaMask connected but accounts unavailable after 3 retries. This may indicate a MetaMask permission issue.');
        }

        setWalletInfo({
          type: walletType,
          address: address,
          isConnected: !!address
        });

        // Stop loading immediately after getting wallet info - icon becomes clickable
        setIsLoading(false);

        // Fetch balance and transactions in background (non-blocking)
        if (address && walletType === WALLET_TYPES.CUSTOM_RPC) {
          // Start both operations in parallel without blocking the UI
          fetchBalance(address).catch(error => console.warn('Balance fetch failed:', error));
          fetchRecentTransactions(address).catch(error => console.warn('Transactions fetch failed:', error));
        }
      } else {
        setWalletInfo(null);
        setBalance(null);
        setTransactions([]);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error loading wallet info:', error);
      setWalletInfo(null);
      setBalance(null);
      setTransactions([]);
      setIsLoading(false);
    }
  };

  const getWalletTypeDisplay = (type) => {
    switch (type) {
      case WALLET_TYPES.METAMASK:
        return 'MetaMask';
      case WALLET_TYPES.CUSTOM_RPC:
        return 'Custom RPC Wallet';
      default:
        return 'Unknown';
    }
  };

  const getWalletIcon = (type) => {
    switch (type) {
      case WALLET_TYPES.METAMASK:
        return (
          <svg className="w-5 h-5 text-orange-600" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          </svg>
        );
      case WALLET_TYPES.CUSTOM_RPC:
        return (
          <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-3a1 1 0 011-1h2.586l6.414-6.414a6 6 0 017.743-5.743L15 7z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        );
    }
  };

  const handleDisconnect = async () => {
    const confirmMessage = 'Disconnect and clear saved private key?';

    if (window.confirm(confirmMessage)) {
      try {
        // Always force clear private keys and saved data
        const activeProvider = walletManager.getActiveProvider();
        if (activeProvider && typeof activeProvider.disconnect === 'function') {
          await activeProvider.disconnect(true); // Force clear
        }
        await walletManager.disconnect();
        setShowDropdown(false);
        if (onDisconnect) onDisconnect();
      } catch (error) {
        console.error('Error disconnecting wallet:', error);
      }
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleShareQR = () => {
    setShowDropdown(false);
    setShowQRModal(true);
  };

  const handleShareLink = async () => {
    if (!walletInfo?.address) return;

    try {
      const walletName = `Wallet ${walletInfo.address.slice(0, 6)}...${walletInfo.address.slice(-4)}`;
      const shareUrl = QRCodeService.generateShareableURL(walletInfo.address, walletName);

      const success = await QRCodeService.copyToClipboard(shareUrl);
      if (success) {
        setShareSuccess('Share link copied!');
        setTimeout(() => setShareSuccess(''), 2000);
      }
    } catch (error) {
      console.error('Failed to generate share link:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full">
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
      </div>
    );
  }

  // Always show clickable button, even when no wallet is connected

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex items-center space-x-2">
        {/* Wallet Address - visible when connected */}
        {walletInfo?.isConnected && walletInfo.address && (
          <div className="hidden sm:block text-xs font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
            {walletInfo.address}
          </div>
        )}

        {/* Wallet Icon */}
      <button
        onClick={() => {
          if (!showDropdown) {
            // Calculate position before showing dropdown
            const position = calculateDropdownPosition();
            setDropdownPosition(position);
          }
          setShowDropdown(!showDropdown);
        }}
        ref={buttonRef}
        className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors wallet-status ${
          walletInfo?.isConnected
            ? 'bg-green-200 border-green-600 hover:bg-green-300 connected'
            : 'bg-red-200 border-red-600 hover:bg-red-300'
        }`}
        data-testid={walletInfo?.isConnected ? "wallet-connected" : "wallet-disconnected"}
        title={walletInfo ?
          `${getWalletTypeDisplay(walletInfo.type)} - ${walletInfo.address?.slice(0,6)}...${walletInfo.address?.slice(-4)}` :
          'No wallet connected - Click for debug info'
        }
      >
        {/* Connection Status Dot */}
        <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${
          walletInfo?.isConnected ? 'bg-green-500' : 'bg-red-500'
        }`}></div>

        {/* Enhanced Wallet Icon */}
        <div className="text-2xl font-bold text-gray-800">💳</div>
      </button>

      {/* Enhanced Dropdown Menu */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className={`absolute right-0 w-80 max-w-[90vw] sm:max-w-md bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[80vh] overflow-y-auto ${
            dropdownPosition === 'above'
              ? 'bottom-full mb-2'
              : 'top-full mt-2'
          }`}
        >
          <div className="p-4">
            {walletInfo ? (
              /* Connected Wallet Section */
              <>
                {/* Wallet Address */}
                <div className="mb-3 pb-3 border-b border-gray-100">
                  <div className="flex items-center mb-2">
                    <div className={`w-3 h-3 rounded-full mr-2 ${
                      walletInfo.isConnected ? 'bg-green-500' : 'bg-red-500'
                    }`}></div>
                    {getWalletIcon(walletInfo.type, "w-6 h-6")}
                    <span className="ml-2 font-medium text-gray-800">
                      {getWalletTypeDisplay(walletInfo.type)}
                    </span>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700">Address:</label>
                    <div className="font-mono text-xs bg-gray-100 p-2 rounded break-all flex items-center justify-between">
                      <AddressDisplayLong address={walletInfo.address} copyable={false} />
                      <button
                        onClick={() => copyToClipboard(walletInfo.address)}
                        className="ml-2 text-blue-600 hover:text-blue-800 flex-shrink-0"
                        title="Copy address"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* No Wallet Connected Section */
              <div className="mb-3 pb-3 border-b border-gray-100">
                <div className="flex items-center mb-2">
                  <div className="w-3 h-3 rounded-full mr-2 bg-red-500"></div>
                  <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="ml-2 font-medium text-gray-800">No Wallet Connected</span>
                </div>

                {/* Debug Information */}
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
                  <div className="flex items-start">
                    <svg className="h-4 w-4 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-xs font-medium text-yellow-800">Connection Status</p>
                      <div className="text-xs text-yellow-700 mt-1">
                        <div className="mb-1"><strong>Wallet Manager State:</strong> No active provider</div>
                        <div className="mb-1"><strong>Available Providers:</strong> MetaMask, Custom RPC</div>
                        <div className="mb-1">
                          <strong>Error Message:</strong>{' '}
                          <span className="font-mono bg-yellow-100 px-1 rounded">
                            {walletManager.getErrorMessage() || 'No specific error'}
                          </span>
                        </div>
                        <div className="mb-1">
                          <strong>Browser Support:</strong>{' '}
                          {typeof window !== 'undefined' && window.ethereum ? 'MetaMask Available' : 'No MetaMask Detected'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Connect Wallet Button */}
                <div className="mb-3">
                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      navigate('/wallet-details');
                    }}
                    className="w-full px-4 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition flex items-center justify-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Connect Wallet
                  </button>
                </div>
              </div>
            )}

            {/* Network Connection Warning - only show for connected wallet with network issues */}
            {walletInfo && !walletInfo.isConnected && (
              <div className="mb-3 bg-yellow-50 border border-yellow-200 rounded p-3">
                <div className="flex items-start">
                  <svg className="h-4 w-4 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-xs font-medium text-yellow-800">Network Connection Issue</p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Your wallet is connected but there may be a network compatibility issue.
                      You can still disconnect to switch wallet types.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {shareSuccess && (
              <div className="mb-3 bg-green-50 border border-green-200 rounded p-3 text-green-700 text-sm">
                {shareSuccess}
              </div>
            )}

            {/* Share Buttons - only show for connected wallets */}
            {walletInfo && (
              <div className="mb-3 grid grid-cols-2 gap-2">
                <button
                  onClick={handleShareQR}
                  className="px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded hover:bg-blue-200 transition flex items-center justify-center"
                  title="Share QR Code"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h2M4 4h5v5H4V4zm11 11h5v5h-5v-5zM4 15h5v5H4v-5z" />
                  </svg>
                  QR Code
                </button>
                <button
                  onClick={handleShareLink}
                  className="px-3 py-2 bg-green-100 text-green-700 text-sm rounded hover:bg-green-200 transition flex items-center justify-center"
                  title="Copy Share Link"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Share Link
                </button>
              </div>
            )}

            {/* Details Button - always show */}
            <div className="mb-3">
              <button
                onClick={() => {
                  setShowDropdown(false);
                  navigate('/wallet-details');
                }}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {walletInfo ? 'View Wallet Details' : 'Go to Wallet Setup'}
              </button>
            </div>

            {/* Wallet Balance - only show for Custom RPC wallets */}
            {walletInfo && walletInfo.type === WALLET_TYPES.CUSTOM_RPC && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="h-4 w-4 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                    <p className="text-xs font-medium text-blue-800">Balance</p>
                  </div>
                  <div className="text-right">
                    {!walletInfo.isConnected ? (
                      <p className="text-xs text-blue-600">Network issue</p>
                    ) : loadingBalance ? (
                      <div className="animate-pulse text-xs text-blue-600">Loading...</div>
                    ) : balance !== null ? (
                      <p className="text-sm font-mono text-blue-900">{parseFloat(balance).toFixed(6)} ETH</p>
                    ) : (
                      <p className="text-xs text-blue-600">Not ready yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Recent Transactions - only show for Custom RPC wallets */}
            {walletInfo && walletInfo.type === WALLET_TYPES.CUSTOM_RPC && (
              <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
                <div className="flex items-center mb-2">
                  <svg className="h-4 w-4 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  <p className="text-xs font-medium text-gray-800">Recent Transactions</p>
                </div>
                {!walletInfo.isConnected ? (
                  <p className="text-xs text-gray-600">Network issue - cannot load transactions</p>
                ) : loadingTransactions ? (
                  <div className="animate-pulse text-xs text-gray-600">Loading...</div>
                ) : transactions.length > 0 ? (
                  <div className="space-y-2">
                    {transactions.slice(0, 3).map((tx, index) => (
                      <div key={tx.hash || index} className="flex items-center justify-between text-xs">
                        <div className="flex items-center">
                          <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                            tx.isIncoming ? 'bg-green-400' : 'bg-red-400'
                          }`}></span>
                          <span className="font-mono text-gray-600">
                            {tx.hash ? `${tx.hash.slice(0, 8)}...` : 'Unknown'}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className={`font-mono ${tx.isIncoming ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.isIncoming ? '+' : '-'}{parseFloat(tx.value).toFixed(4)} POL
                          </div>
                        </div>
                      </div>
                    ))}
                    {transactions.length > 3 && (
                      <p className="text-xs text-gray-500 text-center">+{transactions.length - 3} more in details</p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-600">No recent transactions</p>
                )}
              </div>
            )}

            {/* Action Button - only show disconnect for connected wallets */}
            {walletInfo && (
              <div className="mb-3">
                {walletInfo.type !== WALLET_TYPES.CUSTOM_RPC ? (
                  /* External Wallets - Grey inactive button with generic tooltip */
                  <div className="relative">
                    <button
                      disabled
                      className="w-full px-4 py-2 bg-gray-300 text-gray-500 text-sm rounded cursor-not-allowed flex items-center justify-center"
                      title="Disconnect must be done in wallet extension"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Disconnect in Wallet
                    </button>
                    <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded p-2">
                      <div className="flex items-start">
                        <svg className="h-3 w-3 text-yellow-400 mt-0.5 mr-1 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <p className="text-xs text-yellow-700">
                          To disconnect: Use your wallet extension's Connected Sites feature to disconnect this site
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Custom RPC - Active red disconnect button */
                  <button
                    onClick={handleDisconnect}
                    className="w-full px-4 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition flex items-center justify-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Disconnect
                  </button>
                )}
              </div>
            )}

            {/* Security Warning - only show for Custom RPC wallets */}
            {walletInfo && walletInfo.type === WALLET_TYPES.CUSTOM_RPC && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <div className="flex items-start">
                  <svg className="h-4 w-4 text-blue-400 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-xs font-medium text-blue-800">Custom RPC Storage</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Stored permanently in browser until you disconnect. Click "Disconnect" to completely clear saved key.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      </div> {/* Close flex container */}

      {/* QR Share Modal */}
      <QRModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        address={walletInfo?.address}
        name={`Wallet ${walletInfo?.address?.slice(0, 6)}...${walletInfo?.address?.slice(-4)}`}
        title="Share Your Wallet Address"
      />
    </div>
  );
};

export default WalletConfigIcon;